if (!window.customElements.get('icon-pill')) {

  class MediaGridIcon extends HTMLElement {
    constructor() {
      super();
      this.motion = null;
      this.iconElement = null;
      this.contentElement = null;
      this.backgroundElement = null;
      this.contentWrapper = null;
      this.isExpanded = false;
      this.headerWidth = null;
      this.iconOnlyWidth = null;
      this.hasDialog = false;
      this.hasTrigger = false;
      this.backgroundAnimation = null;
      this.labelAnimation = null;
      this.expandOnView = false;
    }

    connectedCallback() {
      this.initializeElements();
      this.waitForDependencies();
    }

    initializeElements() {
      // Get the icon trigger element
      this.iconElement = this.querySelector('.icon-trigger');
      this.contentElement = this.querySelector('.icon-content');
      this.backgroundElement = this.querySelector('.icon-content__bg');
      this.contentWrapper = this.querySelector('.icon-content__wrapper');
      
      // Check if we have a trigger element
      this.hasTrigger = !!this.iconElement; 
      
      // Check if we have a body element
      const hasBody = !!this.contentElement?.querySelector('.icon-content__body');
      
      if (!this.contentElement) {
        console.warn('MediaGridIcon: No .icon-content element found');
        return;
      }

      if (!this.backgroundElement) {
        console.warn('MediaGridIcon: No .icon-content__bg element found');
        return;
      }

      if (!this.contentWrapper) {
        console.warn('MediaGridIcon: No .content-wrapper element found');
        return;
      }

      // Check if this should be a dialog (only if we have a trigger)
      this.hasDialog = this.hasTrigger && 
                      this.contentElement.hasAttribute('role') && 
                      this.contentElement.getAttribute('role') === 'dialog' &&
                      this.contentElement.hasAttribute('aria-modal') && 
                      this.contentElement.getAttribute('aria-modal') === 'true';

      // Check if we should use hover effects (only when no trigger OR no body)
      this.useHoverEffects = !this.hasTrigger || !hasBody;

      // Check if show-label attribute is present
      this.showLabelAlways = this.hasAttribute('show-label');

      // Check if expand-on-view attribute is present
      this.expandOnView = this.hasDialog && this.hasAttribute('expand-on-view');

      // Measure the header widths
      const headerElement = this.contentElement.querySelector('.icon-content__header');
      if (headerElement) {
        // Calculate full header width by summing all children plus padding
        let totalWidth = headerElement.querySelector("img") ? 38 : 32; // Base padding
        
        // Sum the width of all children
        Array.from(headerElement.children).forEach(child => {
          totalWidth += child.offsetWidth;
        });
        
        this.headerWidth = totalWidth;

        // Calculate icon-only width (just the icon image, not the label)
        const iconImage = headerElement.querySelector('img');
        if (iconImage) {
          this.iconOnlyWidth = 32 + iconImage.offsetWidth; // Base padding + icon width
        } else {
          this.iconOnlyWidth = this.headerWidth; // Fallback to full width if no icon found
        }
      }
    }

    waitForDependencies() {
      // Wait for motion to be ready
      whenReady('motion', (motion) => {
        this.motion = motion;
        if (this.hasDialog) {
          this.initDialog();
        }
        this.setupEventListeners();
        this.initializeAnimations();
      });
    }

    initDialog() {
      // Simple expand/collapse functionality without A11yDialog
      if (!this.hasDialog) return;
      
      // Remove dialog attributes to prevent any dialog behavior
      const dialogContainer = this.querySelector('.icon-content');
      if (dialogContainer) {
        dialogContainer.removeAttribute('role');
        dialogContainer.removeAttribute('aria-modal');
        dialogContainer.removeAttribute('data-a11y-dialog-ignore-focus-trap');
      }
    }

    setupEventListeners() {
      // Add click handler to the icon trigger (only if trigger exists)
      if (this.iconElement) {
        this.iconElement.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (this.hasDialog) {
            // Toggle the expanded state
            if (this.isExpanded) {
              // Collapse
              this.classList.remove('icon-expanded');
              this.isExpanded = false;
              this.iconElement.classList.remove("active");
              this.animateContentCollapse();
            } else {
              // Expand
              this.classList.add('icon-expanded');
              this.isExpanded = true;
              this.iconElement.classList.add("active");
              this.animateContentExpand();
            }
          } else {
            // For non-dialog icons, just toggle the active class for hover effects
            this.iconElement.classList.toggle("active");
            this.toggleHoverState();
          }
        });
      }

      // Add keyboard support (only if trigger exists)
      this.iconElement?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.iconElement.click();
        }
      });

      // Add hover effects for the entire icon-pill element (only if useHoverEffects is true)
      if (this.useHoverEffects) {
        this.addEventListener('mouseenter', () => {
          // console.log('MediaGridIcon: mouseenter triggered');
          this.showHoverState();
        });
        
        this.addEventListener('mouseleave', (e) => {
          // console.log('MediaGridIcon: mouseleave triggered');
          // Always trigger hide on mouseleave, regardless of relatedTarget
          this.hideHoverState();
        });

        // Also add mouseleave to child elements to catch edge cases
        this.contentElement?.addEventListener('mouseleave', (e) => {
          // console.log('MediaGridIcon: content mouseleave triggered');
          if (!this.contains(e.relatedTarget)) {
            this.hideHoverState();
          }
        });
      }
    }

    toggleHoverState() {
      if (this.iconElement && this.iconElement.classList.contains("active")) {
        this.showHoverState();
      } else {
        this.hideHoverState();
      }
    }

    showHoverState() {
      if (!this.motion || !this.backgroundElement || !this.headerWidth) return;
      
      // Stop any existing animations to prevent conflicts
      if (this.backgroundAnimation) {
        this.backgroundAnimation.stop();
      }
      if (this.labelAnimation) {
        this.labelAnimation.stop();
      }
      
      const headerLabel = this.contentElement.querySelector('.icon-content__header-label');
      
      // Expand the background to full header width
      this.backgroundAnimation = this.motion.animate(this.backgroundElement, {
        width: `${this.headerWidth}px`,
        opacity: 1
      }, {
        duration: 0.45,
        easing: 'ease-in-out'
      });

      // Show the header label (always show if showLabelAlways is true)
      if (headerLabel && !this.showLabelAlways) {
        this.labelAnimation = this.motion.animate(headerLabel, {
          opacity: 1
        }, {
          duration: 0.45,
          delay: 0.15,
          easing: 'ease-in-out'
        });
      }
    }

    hideHoverState() {
      if (!this.motion || !this.backgroundElement || !this.iconOnlyWidth) return;
      
      // Stop any existing animations to prevent conflicts
      if (this.backgroundAnimation) {
        this.backgroundAnimation.stop();
      }
      if (this.labelAnimation) {
        this.labelAnimation.stop();
      }
      
      const headerLabel = this.contentElement.querySelector('.icon-content__header-label');
      
      // Contract the background back to icon-only width (unless showLabelAlways is true)
      const targetWidth = this.showLabelAlways ? `${this.headerWidth}px` : `${this.iconOnlyWidth}px`;
      this.backgroundAnimation = this.motion.animate(this.backgroundElement, {
        width: targetWidth,
        opacity: 1 // Keep visible but smaller
      }, {
        duration: 0.2,
        easing: 'ease-in'
      });

      // Hide the header label immediately (no delay) - unless showLabelAlways is true
      if (headerLabel && !this.showLabelAlways) {
        this.labelAnimation = this.motion.animate(headerLabel, {
          opacity: 0
        }, {
          duration: 0.15,
          easing: 'ease-in'
        });
      }
    }

    initializeAnimations() {
      // Set initial state for background element
      if (this.backgroundElement && this.iconOnlyWidth) {
        if (this.hasDialog) {
          // For dialog icons, start visible behind header at full width
          this.backgroundElement.style.width = `${this.headerWidth}px`;
          this.backgroundElement.style.height = '100%';
          this.backgroundElement.style.opacity = '1';
          this.backgroundElement.style.transformOrigin = 'center center';
        } else if (this.useHoverEffects) {
          // For hover-only icons, start at appropriate width based on showLabelAlways
          const initialWidth = this.showLabelAlways ? `${this.headerWidth}px` : `${this.iconOnlyWidth}px`;
          this.backgroundElement.style.width = initialWidth;
          this.backgroundElement.style.height = '100%';
          this.backgroundElement.style.opacity = '1';
          this.backgroundElement.style.transformOrigin = 'center center';
        }
      }

      // Set initial state for content container (only for dialog icons)
      if (this.hasDialog && this.contentElement && this.headerWidth) {
        this.contentElement.style.width = `${this.headerWidth}px`;
        this.contentElement.style.height = '42px';
        this.contentElement.style.transformOrigin = 'left center';
      }

      // Set initial state for body content (only for dialog icons)
      if (this.hasDialog) {
        const bodyElement = this.contentElement.querySelector('.icon-content__body');
        if (bodyElement) {
          bodyElement.style.opacity = '0';
          bodyElement.style.pointerEvents = 'none';
        }
      }

      // Set initial state for header label (for hover-only icons)
      if (this.useHoverEffects) {
        const headerLabel = this.contentElement.querySelector('.icon-content__header-label');
        if (headerLabel) {
          if (this.showLabelAlways) {
            // If show-label attribute is present, keep label visible
            headerLabel.style.opacity = '1';
          } else {
            // Otherwise, hide the label initially
            headerLabel.style.opacity = '0';
          }
        }
      }

      // If expanded attribute is set, open the pill when it enters the viewport
      if (this.expandOnView) {
        const observer = new IntersectionObserver((entries, obs) => {
          if (entries[0].isIntersecting) {
            obs.disconnect();
            this.isExpanded = true;
            this.classList.add('icon-expanded');
            this.iconElement?.classList.add('active');
            this.animateContentExpand();
          }
        }, { threshold: 0.1 });
        observer.observe(this);
      }
    }

    animateContentExpand() {
      if (!this.hasDialog || !this.backgroundElement || !this.motion) return;

      const bodyElement = this.contentElement.querySelector('.icon-content__body');

      // Calculate wrapper height by summing all children heights plus padding
      let wrapperHeight = 16; // 16px padding
      
      // Sum the height of all wrapper children
      Array.from(this.contentWrapper.children).forEach(child => {
        wrapperHeight += child.offsetHeight;
      });
      
      // Use the larger of 207px or the header width
      const expandWidth = Math.max(207, this.headerWidth || 207);
      
      // Animate both the background and content container to expand
      this.motion.animate([this.backgroundElement, this.contentElement], {
        width: `${expandWidth}px`,
        borderRadius: '15px',
        height: `${wrapperHeight}px` // Specific height value instead of auto
      }, {
        duration: 0.4,
        easing: 'ease-out'
      });

      // Animate the body content in
      if (bodyElement) {
        // Calculate body width (expanded width minus padding)
        const bodyWidth = expandWidth - 32; // 32px total padding (16px on each side)
        
        // Start body animation slightly before background animation completes
        setTimeout(() => {
          this.motion.animate(bodyElement, {
            opacity: 1,
            width: `${bodyWidth}px`
          }, {
            duration: 0.3,
            easing: 'ease-out'
          });
          // Enable pointer events when expanded
          bodyElement.style.pointerEvents = 'auto';
        }, 200); // 0.2s delay
      }
    }

    animateContentCollapse() {
      if (!this.hasDialog || !this.backgroundElement || !this.motion) return;

      const bodyElement = this.contentElement.querySelector('.icon-content__body');

      // Animate the body content out first
      if (bodyElement) {
        // Disable pointer events immediately when collapsing
        bodyElement.style.pointerEvents = 'none';
        this.motion.animate(bodyElement, {
          opacity: 0
        }, {
          duration: 0.2,
          easing: 'ease-in'
        });
      }

      // Then animate both background and content back to header size
      // Start slightly before body animation completes
      setTimeout(() => {
        this.motion.animate([this.backgroundElement, this.contentElement], {
          width: `${this.headerWidth}px`, // Return to measured header width
          height: '42px', // Return to initial height
          borderRadius: '30px'
        }, {
          duration: 0.3,
          easing: 'ease-in'
        });
      }, 100); // 0.1s delay
    }

    disconnectedCallback() {
      // Clean up event listeners
      if (this.iconElement) {
        this.iconElement.removeEventListener('click', this.handleIconClick);
        this.iconElement.removeEventListener('keydown', this.handleKeydown);
      }
      this.removeEventListener('mouseenter', this.showHoverState);
      this.removeEventListener('mouseleave', this.hideHoverState);
    }
  }

  customElements.define('icon-pill', MediaGridIcon);
}
